import type { Caption, TikTokPage } from '@remotion/captions';

/**
 * Pure caption transforms, deliberately kept out of pipeline.ts and the
 * composition so they can be exercised in Node without pulling in whisper-web,
 * WebGPU or the DOM. Both bugs these guard against were real.
 */

/**
 * Captions come back relative to the start of whatever audio was fed in, so a
 * sliced range produces timings starting at zero. Shift them onto the real
 * timeline or every caption lands early by the offset.
 */
export const shiftCaptions = (captions: Caption[], offsetMs: number): Caption[] =>
  offsetMs === 0
    ? captions
    : captions.map((c) => ({
        ...c,
        startMs: c.startMs + offsetMs,
        endMs: c.endMs + offsetMs,
        timestampMs: c.timestampMs === null ? null : c.timestampMs + offsetMs,
      }));

const toPage = (tokens: TikTokPage['tokens']): TikTokPage => ({
  text: tokens.map((t) => t.text).join(''),
  startMs: tokens[0].fromMs,
  durationMs: Math.max(tokens[tokens.length - 1].toMs - tokens[0].fromMs, 200),
  tokens,
});

/**
 * Re-paginate. createTikTokStyleCaptions groups purely on elapsed time, which
 * produces two problems: a fast speaker overflows the line, and a page can
 * straddle a sentence boundary ("frame number. This subtitle"). Break on both
 * length and terminal punctuation.
 */
export const repaginate = (pages: TikTokPage[], maxChars: number): TikTokPage[] => {
  const out: TikTokPage[] = [];

  for (const page of pages) {
    let current: TikTokPage['tokens'] = [];
    let chars = 0;

    for (const token of page.tokens) {
      if (current.length > 0 && chars + token.text.length > maxChars) {
        out.push(toPage(current));
        current = [];
        chars = 0;
      }

      current.push(token);
      chars += token.text.length;

      // A sentence ends: flush so the next one starts on its own page.
      if (/[.!?]["')\]]?$/.test(token.text.trim())) {
        out.push(toPage(current));
        current = [];
        chars = 0;
      }
    }

    if (current.length > 0) out.push(toPage(current));
  }

  return out;
};

/**
 * Whisper hallucinates words in silence — "Thank you.", "♪", stray filler.
 * Those get burned permanently into the video, so removing one has to be
 * possible.
 */
export const removeCaption = (captions: Caption[], index: number): Caption[] =>
  index < 0 || index >= captions.length
    ? captions
    : captions.filter((_, i) => i !== index);

/**
 * Nudge a single caption in time, preserving its duration. The clamp matters:
 * a negative startMs puts the caption before the video begins, where the
 * composition's page lookup can never select it, so it silently disappears.
 */
export const nudgeCaption = (
  captions: Caption[],
  index: number,
  deltaMs: number,
): Caption[] => {
  const target = captions[index];
  if (!target) return captions;

  const applied = Math.max(deltaMs, -target.startMs);
  if (applied === 0) return captions;

  return captions.map((c, i) =>
    i === index
      ? {
          ...c,
          startMs: c.startMs + applied,
          endMs: c.endMs + applied,
          timestampMs: c.timestampMs === null ? null : c.timestampMs + applied,
        }
      : c,
  );
};
